import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { take } from 'rxjs/operators';
import { RefListModel } from '../../../models/ref-list-model';
import { DataverseLookup } from '../models/dataverse-lookup';
import { Dto } from '../models/dto';
import { Field } from '../models/fields';
import { AppServiceHelperService } from '../../../services/app-service-helper.service';

/**
 * Dataverse API service
 */
@Injectable({
  providedIn: 'root'
})
export class DataverseApiService {
  /**
   * Resource for the common service.
   */
  private _commonResource: string;

  /**
   * Constructor
   * @param httpClient
   * @param appServiceHelper
   */
  constructor(private httpClient: HttpClient, private appServiceHelper: AppServiceHelperService) {
    this._commonResource = this.appServiceHelper.getServiceEndpoint('pollux.api', 'reflist');
  }

  /**
   * Retrieves the overview data for the DTO groups
   */
  public overview(isDev: boolean, skipLoad = false): Observable<any> {
    if (skipLoad) {
      return of({ devGroups: [], buGroups: [] });
    } else {
      return this.httpClient.get<any>(this.appServiceHelper.getServiceEndpoint('pollux.dataverse.api', `dataverse/v2/data/overview?isDev=${isDev}`));
    }
  }

  /**
 * Retrieves the key user overview data
 * @returns An observable that emits the key user overview data
 */
  public keyUserOverview(): Observable<any> {
    return this.httpClient.get<any>(this.appServiceHelper.getServiceEndpoint('pollux.dataverse.api', `dataverse/v2/data/keyUserOverview`));
  }

  /**
 * Retrieves fields for active DTO
 * @param dtoId - The ID of the DTO
 * @returns An observable that emits an array of fields
 */
  public getFieldsForDTO(dtoId: number): Observable<Field[]> {
    return this.httpClient.get<any>(this.appServiceHelper.getServiceEndpoint('pollux.dataverse.api', `dataverse/v2/data/getFields?dtoId=${dtoId}`));
  }

  /**
   * Retrieves field references based on the field ID
   * @param fieldId - The ID of the field
   * @returns An observable that emits an array of fields
   */
  public fieldReferences(fieldId: number): Observable<Field[]> {
    return this.httpClient.get<Field[]>(this.appServiceHelper.getServiceEndpoint('pollux.dataverse.api', `dataverse/v2/data/field/references?fieldId=${fieldId}`));
  }

  /**
   * Retrieves the reference table based on the table name
   * @param table - The name of the table
   * @returns An observable that emits an array of DataverseLookup objects
   */
  public refTable(table: string): Observable<DataverseLookup[]> {
    return this.httpClient.get<DataverseLookup[]>(this.appServiceHelper.getServiceEndpoint('pollux.dataverse.api', `dataverse/v2/data/refTable?table=${table}`));
  }

  /**
 * Retrieves a reference list by name..
 */
  public getRefList(name: string): Observable<RefListModel[]> {
    return this.httpClient.get<RefListModel[]>(`${this._commonResource}/pollux?reflistName=${name}`);
  }

  /**
   * Retrieves the groups based on the development mode
   * @param isDevelopment - Indicates if it is the development mode
   * @returns An observable that emits an array of DataverseLookup objects
   */
  public getGroups(isDevelopment: boolean): Observable<DataverseLookup[]> {
    return this.httpClient.get<DataverseLookup[]>(this.appServiceHelper.getServiceEndpoint('pollux.dataverse.api', `dataverse/v2/data/group?isDevelopment=${isDevelopment}`));
  }

  /**
   * Validates a field
   * @param field - The field to be validated
   * @returns An observable that emits the validation result
   */
  public validateField(field: any): Observable<any> {
    alert("TODO: Obsolete!");
    field.createdOrModifiedManually = true;
    return this.httpClient.post<any>(this.appServiceHelper.getServiceEndpoint('pollux.dataverse.api', 'dataverse/data/validate'), field);
  }

  /**
   * Creates a new DTO
   * @param dto - The DTO to be created
   * @returns An observable that emits the created DTO
   */
  public createNewDto(dto: Dto): Observable<any> {
    return this.httpClient.post<any>(this.appServiceHelper.getServiceEndpoint('pollux.dataverse.api', `dataverse/v2/data/create`), dto);
  }

  /**
   * Adds a new field
   * @param field - The field to be added
   * @returns An observable that emits the added field
   */
  public addnewField(field: Field): Observable<Field> {
    return this.httpClient.post<Field>(this.appServiceHelper.getServiceEndpoint('pollux.dataverse.api', `dataverse/v2/data/field/create`), field);
  }

  /**
   * Edits a field
   * @param field - The field to be edited
   * @returns An observable that emits the edited field
   */
  public editField(field: Field): Observable<Field> {
    return this.httpClient.post<Field>(this.appServiceHelper.getServiceEndpoint('pollux.dataverse.api', `dataverse/v2/data/field/edit`), field);
  }

  /**
   * Deletes a field
   * @param field - The field to be deleted
   * @returns An observable that emits a boolean indicating the success of the deletion
   */
  public deleteField(field: Field): Observable<boolean> {
    return this.httpClient.post<boolean>(this.appServiceHelper.getServiceEndpoint('pollux.dataverse.api', `dataverse/v2/data/field/delete`), field);
  }

  /**
   * Deletes a DTO
   * @param dto
   * @returns
   */
  public deleteDTO(dto: Dto): Observable<boolean> {
    return this.httpClient.post<boolean>(this.appServiceHelper.getServiceEndpoint('pollux.dataverse.api', `dataverse/v2/data/delete`), dto);
  }

  /**
   * Edits a DTO
   * @param dto - The DTO to be edited
   * @returns An observable that emits the edited DTO
   */
  public edit(dto: Dto): Observable<Dto> {
    return this.httpClient.post<Dto>(this.appServiceHelper.getServiceEndpoint('pollux.dataverse.api', `dataverse/v2/data/edit`), dto);
  }

  /**
   * Edits the DTO group
   * @param isDevelopment - Indicates if it is the development mode
   * @param dtoId - The ID of the DTO
   * @param groupId - The ID of the group
   * @returns An observable that emits a boolean indicating the success of the operation
   */
  public editDtoGroup(isDevelopment: boolean, dtoId: number, groupId: number): Observable<boolean> {
    return this.httpClient.post<boolean>(this.appServiceHelper.getServiceEndpoint('pollux.dataverse.api', `dataverse/v2/data/updateDtoGroup?isDevelopment=${isDevelopment}&dtoId=${dtoId}&groupId=${groupId}`), null);
  }

  /**
   * Retrieves the system fields
   * @returns An observable that emits an array of system fields
   */
  public getSystemFields(): Observable<any[]> {
    return this.httpClient.get<any[]>(this.appServiceHelper.getServiceEndpoint('pollux.dataverse.api', `dataverse/v2/data/getSystemFields`));
  }

  /**
   * Retrieves a field based on the unique name source
   * @param uniqueNameSource - The unique name source of the field
   * @returns An observable that emits the field
   */
  public getField(uniqueNameSource): Observable<any> {
    return this.httpClient.get<any[]>(this.appServiceHelper.getServiceEndpoint('pollux.dataverse.api', `dataverse/v2/data/getField?uniqueNameSource=${uniqueNameSource}`));
  }

  /**
   * Retrieves a field based on the field ID
   * @param fieldId - The ID of the field
   * @returns An observable that emits the field
   */
  public getFieldById(fieldId): Observable<any> {
    return this.httpClient.get<any[]>(this.appServiceHelper.getServiceEndpoint('pollux.dataverse.api', `dataverse/v2/data/getFieldById?fieldId=${fieldId}`));
  }

  /**
   * Saves an export template
   * @param template - The export template to be saved
   * @returns An observable that emits the saved DTO
   */
  public saveExportTemplate(template): Observable<any> {
    return this.httpClient.post<Dto>(this.appServiceHelper.getServiceEndpoint('pollux.import.api', `export/v2/template`), template);
  }

  /**
   * Saves a copied export template
   * @param template - The export template to be saved
   * @param sourceTemplateId - The ID of the source template
   * @returns An observable that emits the saved DTO
   */
  public saveExportTemplateCopy(template: any, sourceTemplateId: number): Observable<any> {
    return this.httpClient.post<Dto>(this.appServiceHelper.getServiceEndpoint('pollux.import.api', `export/v2/template/copy?sourceTemplateId=${sourceTemplateId}`), template);
  }

  /**
   * Retrieves all export templates
   * @returns An observable that emits an array of export templates
   */
  public getExportTemplates(): Observable<any[]> {
    return this.httpClient.get<any[]>(this.appServiceHelper.getServiceEndpoint('pollux.import.api', 'export/v2/templates'));
  }

  /**
   * Retrieves the export template for a specific DTO
   * @param dtoId - The ID of the DTO
   * @returns An observable that emits the export template
   */
  public getExportTemplateByDto(dtoId: number): Observable<any> {
    return this.httpClient.get<any>(this.appServiceHelper.getServiceEndpoint('pollux.import.api', `export/v2/template/dto?dtoId=${dtoId}`));
  }

  /**
   * Retrieves the export template based on the template ID
   * @param templateId - The ID of the template
   * @returns An observable that emits the export template
   */
  public getExportTemplate(templateId: number): Observable<any> {
    return this.httpClient.get<any>(this.appServiceHelper.getServiceEndpoint('pollux.import.api', `export/v2/template?templateId=${templateId}`));
  }

  /**
   * Publishes or unpublishes a template
   * @param publish - Indicates if the template should be published or unpublished
   * @param templateId - The ID of the template
   * @returns An observable that emits the result of the operation
   */
  public publishTemplate(publish: boolean, templateId: number) {
    const action = publish ? 'publish' : 'unpublish';
    return this.httpClient.post<any>(this.appServiceHelper.getServiceEndpoint('pollux.import.api', `export/v2/templates/${action}`), { "templateIds": [templateId] });
  }

  /**
   * Retrieves the validation result for a specific DTO
   * @param activeDtoId - The ID of the active DTO
   * @returns An observable that emits the validation result
   */
  getValidationResult(activeDtoId: number) {
    return this.httpClient.get<any>(this.appServiceHelper.getServiceEndpoint('pollux.dataverse.api', `dataverse/v2/data/validate?dtoId=${activeDtoId}`))
  }

  /**
   * Exports selected fields or all fields of a business object
   * @param selectedFields - The selected fields to be exported
   * @param boName - The name of the business object (optional)
   */
  public export(selectedFields?: any, boName?: string,) {
    const query = boName ? `?boName=${boName}` : "";
    this.httpClient.post(this.appServiceHelper.getServiceEndpoint('pollux.dataverse.api', `dataverse/v2/data/export${query}`), selectedFields, { responseType: 'blob' }).pipe(take(1)).subscribe((result: any) => {
      if (result) {
        if (!boName) {
          this.initiateDownload(result, 'Export.xlsx');
        } else {
          this.initiateDownload(result, boName + '.xlsx');
        }
      } else {
        alert("Could not save the field: " + result.message);
      }
    });
  }

  /**
   * Initiate a download
   * @param blob BLOB item
   * @param alternateFilename File name
   */
  initiateDownload(blob: any, alternateFilename: string) {
    const element = document.createElement('a');
    element.setAttribute('href', window.URL.createObjectURL(blob));
    element.setAttribute('download', alternateFilename);
    element.style.display = 'none';
    document.body.appendChild(element);
    setTimeout(() => {
      element.click();
      setTimeout(() => {
        document.body.removeChild(element);
      }, 100);
    }, 150);
  }

  /**
   * Returns change request json
   * @param fieldId Field id
   */
  public getChangeRequestJson(fieldId: number): Observable<any> {
    return this.httpClient.get<any>(this.appServiceHelper.getServiceEndpoint('pollux.dataverse.api', `dataverse/v2/data/field/changeRequest?fieldId=${fieldId}`));
  }

  /**
   * Returns an array containing all the fields that contain searchTerm
   * @param searchTerm
   * @param considerUniqueNameInSearch
   * @returns
   */
  public SearchAllFields(searchTerm: string, considerUniqueNameInSearch: boolean): Observable<any> {
    const encodedSearchTerm = encodeURIComponent(searchTerm);
    return this.httpClient.get<any>(
      this.appServiceHelper.getServiceEndpoint(
        'pollux.dataverse.api',
        `dataverse/v2/data/SearchAllFields?searchTerm=${encodedSearchTerm}&considerUniqueNameInSearch=${considerUniqueNameInSearch}`
      )
    );
  }

}
